import { Application } from "../Application/Application";
import "./OS.css";
import bg_img from "url:./background.jpg";
import React, { useCallback, useState, useEffect } from "react";
import { TaskBar } from "../TaskBar/TaskBar";
import { AppGallery } from "../AppsGallery/AppsGallery";
import { LoginScreen } from "../LoginScreen/LoginScreen";

const MIN_APP_WIDTH = window.innerWidth < 600 ? 420 : 820;
const MIN_APP_HEIGHT = window.innerWidth < 600 ? 320 : 620;

export function OS({
    path,
    defaultApplications = [
        {
            command: "interactive-cv",
            posX: 200,
            posY: 200,
            isMaximized: true,
            isMinimized: false,
        },
    ],
}) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(() =>
        navigator.language.toLowerCase().startsWith("fr") ? "Fr" : "En"
    );
    const [applications, setApplications] = useState({});
    const [focusedApp, setFocusedApp] = useState(-1);
    const [currentZIndex, setCurrentZIndex] = useState(0);

    const [currentResizing, setCurrentResizing] = useState(-1);
    const [currentMoving, setCurrentMoving] = useState(-1);

    const [taskbarMaximized, setTaskbarMaximized] = useState(false);
    const [openAppGallery, setOpenAppGallery] = useState(false);
    const [closingAppGallery, setClosingAppGallery] = useState(false);

    const openApp = (
        command,
        args = null,
        posX = null,
        posY = null,
        width = null,
        height = null,
        isMaximized = null,
        isMinimized = null,
        component = null,
        children = null,
        icon = null,
        title = null,
        forceZIndex = null
    ) => {
        const app = path.find((app) => app.command === command);

        const index = forceZIndex !== null ? forceZIndex : currentZIndex + 1;

        if (app) {
            const newApp = {
                args: args || app.defaultargs || {},
                pid: forceZIndex || currentZIndex,
                title: title || app.title,
                icon: icon || app.icon,
                posX: posX !== null ? posX : 30 * ((index % 13) + 1),
                posY: posY !== null ? posY : 30 * ((index % 13) + 2),
                minWidth: app.minWidth || MIN_APP_WIDTH,
                minHeight: app.minHeight || MIN_APP_HEIGHT,
                width: width !== null ? width : (app.minWidth || MIN_APP_WIDTH),
                height: height !== null ? height : (app.minHeight || MIN_APP_HEIGHT),
                isMaximized: isMaximized !== null ? isMaximized : false,
                isMinimized: isMinimized !== null ? isMinimized : false,
                isClosing: false,
                zIndex: forceZIndex || currentZIndex,
                isMoving: false,
                isResizing: false,
                isTransitioning: false,
                startX: -1,
                startY: -1,
                resizeType: null,
                component: component || app.component || Application,
                children: children || app.children || null,
            };
            setApplications((prevApps) => ({
                ...prevApps,
                [newApp.pid]: newApp,
            }));
            setCurrentZIndex((prevZIndex) => prevZIndex + 1);
            setFocusedApp(newApp.pid);
        } else {
            console.error(`Application with command "${command}" not found.`);
        }
    };

    const hasInitialized = React.useRef(false);

    useEffect(() => {
        if (
            !hasInitialized.current &&
            defaultApplications &&
            defaultApplications.length > 0
        ) {
            defaultApplications.forEach((app, index) => {
                openApp(
                    app.command,
                    app.defaultargs,
                    app.posX,
                    app.posY,
                    app.width,
                    app.height,
                    app.isMaximized,
                    app.isMinimized,
                    app.component,
                    app.children,
                    app.icon,
                    app.title,
                    index
                );
            });
            hasInitialized.current = true;
        }
    }, [defaultApplications]);

    const onMouseMove = useCallback((pid) => (e) => {
        if (currentMoving === pid) {
            setApplications((prevApps) => {
                const updatedApps = { ...prevApps };
                updatedApps[pid].posX =
                    e.clientX - updatedApps[pid].startX < 0
                        ? 0
                        : e.clientX - updatedApps[pid].startX;
                updatedApps[pid].posY =
                    e.clientY - updatedApps[pid].startY < 0
                        ? 0
                        : e.clientY - updatedApps[pid].startY;
                updatedApps[pid].zIndex = currentZIndex;
                return updatedApps;
            });
        } else if (currentResizing === pid) {
            setApplications((prevApps) => {
                const updatedApps = { ...prevApps };
                const app = updatedApps[pid];
                const resize_type = app.resizeType;
                const minW = app.minWidth;
                const minH = app.minHeight;
                switch (resize_type) {
                    case "top-left":
                        app.posX =
                            app.initialWidth + (app.initialPosX - e.clientX) <
                            minW
                                ? app.posX
                                : e.clientX;
                        app.posY =
                            app.initialHeight + (app.initialPosY - e.clientY) <
                            minH
                                ? app.posY
                                : e.clientY;
                        app.width =
                            app.initialWidth + (app.initialPosX - e.clientX) <
                            minW
                                ? minW
                                : app.initialWidth +
                                  (app.initialPosX - e.clientX);
                        app.height =
                            app.initialHeight + (app.initialPosY - e.clientY) <
                            minH
                                ? minH
                                : app.initialHeight +
                                  (app.initialPosY - e.clientY);
                        break;
                    case "top-right":
                        app.posY =
                            app.initialHeight + (app.initialPosY - e.clientY) <
                            minH
                                ? app.posY
                                : e.clientY;
                        app.width =
                            e.clientX - app.initialPosX < minW
                                ? minW
                                : e.clientX - app.initialPosX;
                        app.height =
                            app.initialHeight + (app.initialPosY - e.clientY) <
                            minH
                                ? minH
                                : app.initialHeight +
                                  (app.initialPosY - e.clientY);
                        break;
                    case "bottom-left":
                        app.posX =
                            app.initialWidth + (app.initialPosX - e.clientX) <
                            minW
                                ? app.posX
                                : e.clientX;
                        app.width =
                            app.initialWidth + (app.initialPosX - e.clientX) <
                            minW
                                ? minW
                                : app.initialWidth +
                                  (app.initialPosX - e.clientX);
                        app.height =
                            e.clientY - app.initialPosY < minH
                                ? minH
                                : e.clientY - app.initialPosY;
                        break;
                    case "bottom-right":
                        app.width =
                            e.clientX - app.initialPosX < minW
                                ? minW
                                : e.clientX - app.initialPosX;
                        app.height =
                            e.clientY - app.initialPosY < minH
                                ? minH
                                : e.clientY - app.initialPosY;
                        break;
                    case "top":
                        app.posY =
                            app.initialHeight + (app.initialPosY - e.clientY) <
                            minH
                                ? app.posY
                                : e.clientY;
                        app.height =
                            app.initialHeight + (app.initialPosY - e.clientY) <
                            minH
                                ? minH
                                : app.initialHeight +
                                  (app.initialPosY - e.clientY);
                        break;
                    case "bottom":
                        app.height =
                            e.clientY - app.initialPosY < minH
                                ? minH
                                : e.clientY - app.initialPosY;
                        break;
                    case "left":
                        app.posX =
                            app.initialWidth + (app.initialPosX - e.clientX) <
                            minW
                                ? app.posX
                                : e.clientX;
                        app.width =
                            app.initialWidth + (app.initialPosX - e.clientX) <
                            minW
                                ? minW
                                : app.initialWidth +
                                  (app.initialPosX - e.clientX);
                        break;
                    case "right":
                        app.width =
                            e.clientX - app.initialPosX < minW
                                ? minW
                                : e.clientX - app.initialPosX;
                        break;
                    default:
                        break;
                }
                return updatedApps;
            });
        }
    });

    const onClose = useCallback((pid) => (e) => {
        setApplications((prevApps) => {
            const updatedApps = { ...prevApps };
            updatedApps[pid].isClosing = true;
            return updatedApps;
        });
        setTimeout(() => {
            setApplications((prevApps) => {
                const updatedApps = { ...prevApps };
                delete updatedApps[pid];
                return updatedApps;
            });
        }, 300);
        if (focusedApp === pid) {
            const remainingPids = Object.keys(applications).filter(
                (key) => key !== pid
            );
            setFocusedApp(
                remainingPids.length > 0
                    ? parseInt(remainingPids[remainingPids.length - 1])
                    : -1
            );
        }
    });

    const onFocus = useCallback((pid) => (e) => {
        setFocusedApp(pid);
        setCurrentZIndex((prevZIndex) => prevZIndex + 1);
        setApplications((prevApps) => {
            const updatedApps = { ...prevApps };
            updatedApps[pid].zIndex = currentZIndex;

            if (updatedApps[pid].isMinimized) {
                onMinimize(pid)(e);
            }

            return updatedApps;
        });
    });

    const onMinimize = useCallback((pid) => (e) => {
        setApplications((prevApps) => {
            const updatedApps = { ...prevApps };
            updatedApps[pid].isTransitioning = true;
            return updatedApps;
        });

        setTimeout(() => {
            setApplications((prevApps) => {
                const updatedApps = { ...prevApps };
                updatedApps[pid].isMinimized = !updatedApps[pid].isMinimized;

                return updatedApps;
            });

            setTimeout(() => {
                setApplications((prevApps) => {
                    const updatedApps = { ...prevApps };
                    updatedApps[pid].isTransitioning = false;
                    return updatedApps;
                });
            }, 320);
        }, 1);
    });

    const onMaximize = useCallback((pid) => (e) => {
        const taskbarUpdate =
            Object.entries(applications).filter(([key, app]) => {
                return app.isMaximized && app.pid != pid;
            }).length < 1;

        setApplications((prev) => ({
            ...prev,
            [pid]: {
                ...prev[pid],
                isTransitioning: true,
            },
        }));

        setTimeout(() => {
            setApplications((prev) => {
                const isMaximized = !prev[pid].isMaximized;
                return {
                    ...prev,
                    [pid]: {
                        ...prev[pid],
                        isMaximized: isMaximized,
                    },
                };
            });

            setTimeout(() => {
                setApplications((prev) => ({
                    ...prev,
                    [pid]: {
                        ...prev[pid],
                        isTransitioning: false,
                    },
                }));
            }, 310);
        }, 1);
    });

    const onLanguageChange = useCallback(() => {
        setSelectedLanguage((prevLanguage) =>
            prevLanguage === "Fr" ? "En" : "Fr"
        );
    });

    const onCloseAppGallery = useCallback(() => {
        setClosingAppGallery(true);
        setTimeout(() => {
            setOpenAppGallery(false);
            setClosingAppGallery(false);
        }, 310);
    });

    const onOpenAppGallery = useCallback(() => {
        if (!openAppGallery) {
            setOpenAppGallery(true);
            setTaskbarMaximized(true);
        } else {
            onCloseAppGallery();
        }
    }, [openAppGallery]);

    useEffect(() => {
        if (openAppGallery && !closingAppGallery) {
            setTaskbarMaximized(true);
            return;
        }

        for (const [key, app] of Object.entries(applications)) {
            if (app.isMaximized && !app.isMinimized) {
                setTaskbarMaximized(true);
                return;
            }
        }
        setTaskbarMaximized(false);
    }, [applications, openAppGallery, closingAppGallery]);

    return (
        <div
            className="os"
            style={{ backgroundImage: `url(${bg_img})` }}
            aria-hidden={!isLoggedIn}
            onMouseMove={(e) => {
                if (currentMoving !== -1 || currentResizing !== -1)
                    onMouseMove(
                        currentMoving !== -1 ? currentMoving : currentResizing
                    )(e);
            }}
            onMouseUp={() => {
                if (currentMoving !== -1) {
                    setCurrentMoving(-1);
                    setApplications((prevApps) => {
                        const updatedApps = { ...prevApps };
                        updatedApps[currentMoving].startX = -1;
                        updatedApps[currentMoving].startY = -1;
                        return updatedApps;
                    });
                }
                if (currentResizing !== -1) {
                    setCurrentResizing(-1);
                    setApplications((prevApps) => {
                        const updatedApps = { ...prevApps };
                        updatedApps[currentResizing].initialPosX = -1;
                        updatedApps[currentResizing].initialPosY = -1;
                        updatedApps[currentResizing].initialWidth = -1;
                        updatedApps[currentResizing].initialHeight = -1;
                        updatedApps[currentResizing].resizeType = null;
                        updatedApps[currentResizing].startX = -1;
                        updatedApps[currentResizing].startY = -1;
                        return updatedApps;
                    });
                }
            }}
            onTouchMove={(e) => {
                if (currentMoving !== -1 || currentResizing !== -1)
                    onMouseMove(
                        currentMoving !== -1 ? currentMoving : currentResizing
                    )(e.touches[0]);
            }}
            onTouchEnd={() => {
                if (currentMoving !== -1) {
                    setCurrentMoving(-1);
                    setApplications((prevApps) => {
                        const updatedApps = { ...prevApps };
                        updatedApps[currentMoving].startX = -1;
                        updatedApps[currentMoving].startY = -1;
                        return updatedApps;
                    });
                }
                if (currentResizing !== -1) {
                    setCurrentResizing(-1);
                    setApplications((prevApps) => {
                        const updatedApps = { ...prevApps };
                        updatedApps[currentResizing].initialPosX = -1;
                        updatedApps[currentResizing].initialPosY = -1;
                        updatedApps[currentResizing].initialWidth = -1;
                        updatedApps[currentResizing].initialHeight = -1;
                        updatedApps[currentResizing].resizeType = null;
                        updatedApps[currentResizing].startX = -1;
                        updatedApps[currentResizing].startY = -1;
                        return updatedApps;
                    });
                }
            }}
        >
            {!isLoggedIn && (
                <LoginScreen onLogin={() => setIsLoggedIn(true)} />
            )}
            <div className="os_content">
                {openAppGallery && (
                    <AppGallery
                        applications={path}
                        onClose={onCloseAppGallery}
                        onOpenApp={openApp}
                        closing={closingAppGallery}
                        zIndex={currentZIndex + 1}
                        language={selectedLanguage}
                    />
                )}

                {Object.entries(applications).map(([key, app]) =>
                    React.createElement(
                        app.component,
                        {
                            key: app.pid,
                            pid: app.pid,
                            args: app.args,
                            title: app.title,
                            icon: app.icon,
                            posX: app.posX,
                            posY: app.posY,
                            width: app.width,
                            height: app.height,
                            zIndex: app.zIndex,
                            isMaximized: app.isMaximized,
                            isMinimized: app.isMinimized,
                            isMoving: currentMoving === app.pid,
                            isResizing: currentResizing === app.pid,
                            isFocused: focusedApp === app.pid,
                            isClosing: app.isClosing,
                            isTransitioning: app.isTransitioning,
                            language: selectedLanguage,
                            onFocusFunc: onFocus(app.pid),
                            onCloseFunc: onClose(app.pid),
                            onMinimizeFunc: onMinimize(app.pid),
                            onMaximizeFunc: onMaximize(app.pid),
                            onStartDragFunc: (e) => {
                                setCurrentMoving(app.pid);
                                setApplications((prevApps) => {
                                    const updatedApps = { ...prevApps };
                                    updatedApps[app.pid].startX =
                                        e.clientX - updatedApps[app.pid].posX;
                                    updatedApps[app.pid].startY =
                                        e.clientY - updatedApps[app.pid].posY;
                                    return updatedApps;
                                });
                            },
                            onEndDragFunc: () => {
                                setCurrentMoving(-1);
                                setApplications((prevApps) => {
                                    const updatedApps = { ...prevApps };
                                    updatedApps[app.pid].startX =
                                        updatedApps[app.pid].posX;
                                    updatedApps[app.pid].startY =
                                        updatedApps[app.pid].posY;
                                    return updatedApps;
                                });
                            },
                            onStartResizeFunc: (resize_type) => (e) => {
                                setCurrentResizing(app.pid);
                                setApplications((prevApps) => {
                                    const updatedApps = { ...prevApps };
                                    updatedApps[app.pid].initialPosX =
                                        updatedApps[app.pid].posX;
                                    updatedApps[app.pid].initialPosY =
                                        updatedApps[app.pid].posY;
                                    updatedApps[app.pid].initialWidth =
                                        updatedApps[app.pid].width;
                                    updatedApps[app.pid].initialHeight =
                                        updatedApps[app.pid].height;
                                    updatedApps[app.pid].startX = e.clientX;
                                    updatedApps[app.pid].startY = e.clientY;
                                    updatedApps[app.pid].resizeType =
                                        resize_type;
                                    return updatedApps;
                                });
                            },
                            onEndResizeFunc: () => {
                                setCurrentResizing(-1);
                                setApplications((prevApps) => {
                                    const updatedApps = { ...prevApps };
                                    updatedApps[app.pid].initialPosX = -1;
                                    updatedApps[app.pid].initialPosY = -1;
                                    updatedApps[app.pid].initialWidth = -1;
                                    updatedApps[app.pid].initialHeight = -1;
                                    updatedApps[app.pid].startX = -1;
                                    updatedApps[app.pid].startY = -1;
                                    updatedApps[app.pid].resizeType = null;
                                    return updatedApps;
                                });
                            },
                            onMouseMoveFunc: onMouseMove(app.pid),
                            openApp: openApp,
                        },
                        app.children || null
                    )
                )}
                <TaskBar
                    appsOpened={applications}
                    onOpenClick={onFocus}
                    isMaximized={taskbarMaximized}
                    zIndex={currentZIndex + 1}
                    language={selectedLanguage}
                    onOpenAppGallery={onOpenAppGallery}
                    handleLanguageChange={onLanguageChange}
                />
            </div>
        </div>
    );
}
